import { useCallback, useMemo } from 'react'
import { useParams, useRouteMatch } from 'react-router'
import {
  RouteParams,
  isDocumentPageRouteCanvas,
  isDocumentRoute,
  isFrameGroupRoute,
  isPreviewFrameCanvasRoute,
  isPreviewPageCanvasRoute,
  routes,
  versionedRoutes,
} from '@sketch/modules-common'
import { useVersioning } from '../../../versioning'
import {
  PreviewContextProps,
  usePreview,
} from '../../../shares/views/PreviewView/PreviewContext'
import { ErrorHandler } from '@sketch/tracing'

export function useCreateLayerLink() {
  const resolveFullPathname = useResolveFullCurrentPathname()

  return useCallback(
    (layerPathHash: string) => {
      // A layer deep-link should always be tied to a specific version
      // since it could appear/disappear based on the document version.
      const pathname = resolveFullPathname()

      if (!pathname) {
        return null
      }

      return `${window.location.origin}${pathname}?layerPath=${layerPathHash}#Inspect`
    },
    [resolveFullPathname]
  )
}

/**
 * Our views support different routes where some query parameters are optional
 * and are inferred (like the optional page UUID or the version id). This hook returns
 * a function that will resolve the full pathname based on the current router pathname,
 * making sure all the parameters are present in the returned pathname.
 *
 * The 3 views supported are:
 * - Page Canvas View (with or without page UUID)
 * - Frame View
 * - Preview View (with or without page UUID)
 */
function useResolveFullCurrentPathname() {
  const { path } = useRouteMatch()
  const versioning = useVersioning()
  const preview = usePreview()
  const params: unknown = useParams()

  return useCallback(() => {
    // Each of these functions will return null if the current path doesn't match
    // their respective routes.
    const pathname =
      maybeResolvePageCanvasFullPathname(path, params, versioning) ||
      maybeResolveFrameFullPathname(path, params, versioning) ||
      maybeResolvePreviewPageFullPathname(path, params, preview) ||
      maybeResolvePreviewFrameFullPathname(path, params, preview)

    if (pathname === null) {
      ErrorHandler.shouldNeverHappen(
        'Cannot create layer link for unsupported route'
      )
      return null
    }

    return pathname
  }, [path, params, versioning, preview])
}

/**
 * Create the versioned pathname when on a preview route.
 * - PREVIEW_PAGE_VIEW (page UUID defined).
 * - PREVIEW_VIEW (no page UUID defined).
 */
function maybeResolvePreviewPageFullPathname(
  path: string,
  params: unknown,
  preview: PreviewContextProps | undefined
): string | null {
  if (!preview) {
    return null
  }

  if (path === routes.PREVIEW_PAGE_VIEW.template()) {
    const previewParams = params as RouteParams<'PREVIEW_PAGE_VIEW'>
    return routes.PREVIEW_PAGE_VIEW.create({
      previewID: previewParams.previewID,
      pageUUID: previewParams.pageUUID,
    })
  }

  // Route without page UUID defined
  if (path === routes.PREVIEW_VIEW.template()) {
    const previewParams = params as RouteParams<'PREVIEW_VIEW'>
    if (!preview.currentPage?.uuid) {
      // We can assume that everything is loaded before the user can click on a layer
      // to create a link.
      ErrorHandler.shouldNeverHappen(
        'Cannot create layer link when current preview page is loading'
      )
      return null
    }

    return routes.PREVIEW_PAGE_VIEW.create({
      previewID: previewParams.previewID,
      pageUUID: preview.currentPage.uuid,
    })
  }

  return null
}

/**
 * Create a full pathname when on a preview frame view route.
 * This route always has the page and frame UUID defined
 */
function maybeResolvePreviewFrameFullPathname(
  path: string,
  params: unknown,
  preview: PreviewContextProps | undefined
): string | null {
  if (!preview) {
    return null
  }

  if (path === routes.PREVIEW_FRAME_VIEW.template()) {
    const previewParams = params as RouteParams<'PREVIEW_FRAME_VIEW'>
    return routes.PREVIEW_FRAME_VIEW.create({
      previewID: previewParams.previewID,
      pageUUID: previewParams.pageUUID,
      frameUUID: previewParams.frameUUID,
    })
  }

  return null
}

/**
 * Create the versioned pathname when on a page canvas view route.
 * - SHARE_PAGE_CANVAS_VIEW (with page UUID).
 * - SHARE_VIEW (without page UUID).
 */
function maybeResolvePageCanvasFullPathname(
  path: string,
  params: unknown,
  versioning: ReturnType<typeof useVersioning>
): string | null {
  if (isDocumentPageRouteCanvas(path)) {
    const pageParams = params as RouteParams<'SHARE_PAGE_CANVAS_VIEW'>
    if (versioning.loading) {
      ErrorHandler.shouldNeverHappen(
        'Cannot create layer link when versioning is loading'
      )
      return null
    }

    return versionedRoutes.SHARE_PAGE_CANVAS_VIEW.VERSION.create({
      shareID: versioning.share.identifier,
      pageUUID: pageParams.pageUUID,
      versionShortId: versioning.currentVersion.shortId,
    })
  }

  // Route without page UUID defined, default to first page
  if (isDocumentRoute(path)) {
    if (versioning.loading) {
      ErrorHandler.shouldNeverHappen(
        'Cannot create layer link when versioning is loading'
      )
      return null
    }
    const currentVersion = versioning.currentVersion

    // When we are on the SHARE_VIEW route, it defaults to the first page.
    const firstPageId = currentVersion?.document?.pages?.entries[0]?.uuid
    if (!firstPageId) {
      ErrorHandler.shouldNeverHappen(
        'Cannot create layer link when document has no pages'
      )
      return null
    }

    return versionedRoutes.SHARE_PAGE_CANVAS_VIEW.VERSION.create({
      shareID: versioning.share.identifier,
      pageUUID: firstPageId,
      versionShortId: versioning.currentVersion.shortId,
    })
  }

  return null
}

/**
 * Create the versioned pathname when on a frame view route.
 */
function maybeResolveFrameFullPathname(
  path: string,
  params: unknown,
  versioning: ReturnType<typeof useVersioning>
) {
  if (isFrameGroupRoute(path)) {
    const frameParams = params as RouteParams<'FRAME'>
    if (versioning.loading) {
      ErrorHandler.shouldNeverHappen(
        'Cannot create layer link when versioning is loading'
      )
      return null
    }

    return versionedRoutes.FRAME.VERSION.create({
      shareID: versioning.share.identifier,
      frameUUID: frameParams.frameUUID,
      versionShortId: versioning.currentVersion.shortId,
    })
  }

  return null
}

/**
 * We only support the copy link for the most recent views and
 * ignore legacy views for simplicity when creating links later.
 */
export function useShouldEnableCopyLinkToLayer() {
  const { path } = useRouteMatch()

  return useMemo(() => {
    return (
      isDocumentRoute(path) ||
      isDocumentPageRouteCanvas(path) ||
      isFrameGroupRoute(path) ||
      isPreviewPageCanvasRoute(path) ||
      isPreviewFrameCanvasRoute(path)
    )
  }, [path])
}
